import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CalendarRange, ChevronLeft, ChevronRight, Users, CalendarPlus } from 'lucide-react';
import api from '../../services/api';
import { addDaysKey, weekRangeText, groupColor, israelTodayKey, currentWeekKey, dateKeyToDate } from '../../utils/sportSchedule';

const COUNT_COLORS = {
  confirmed: ['var(--success-50, #ecfdf5)', 'var(--success-700, #047857)'],
  declined: ['var(--danger-50, #fef2f2)', 'var(--danger-700, #b91c1c)'],
  pending: ['var(--warning-50, #fffbeb)', 'var(--warning-700, #b45309)'],
};

/**
 * Sport dashboard: "This week's trainings" — always shown. Sunday–Saturday of
 * the week (Israel), prev / next arrows, each day's sessions coloured by group
 * with time, group, coach, how many clients and their confirmations. Today is
 * highlighted; a session opens its attendance page. On a phone the days
 * scroll sideways. GET /api/sport/week.
 */
export default function SportWeekCard({ refreshKey = 0, onScheduleWeek }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const isRTL = lang === 'ar' || lang === 'he';
  const navigate = useNavigate();
  const [weekKey, setWeekKey] = useState(() => currentWeekKey());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/sport/week?weekKey=${weekKey}`)
      .then((r) => { setData(r?.data || r || null); setFailed(false); })
      .catch(() => { setData(null); setFailed(true); })
      .finally(() => setLoading(false));
  }, [weekKey]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const today = israelTodayKey();
  const sessions = (data && data.weekKey === weekKey && data.sessions) || [];
  const dayTitle = (key) => {
    try { return new Intl.DateTimeFormat(lang, { weekday: 'short', day: 'numeric', month: 'numeric', timeZone: 'UTC' }).format(dateKeyToDate(key)); } catch { return key; }
  };
  const countChip = (k, n) => ({
    display: 'inline-flex', alignItems: 'center', padding: '1px 6px', borderRadius: 999, fontSize: '0.7rem', fontWeight: 700,
    background: COUNT_COLORS[k][0], color: COUNT_COLORS[k][1], whiteSpace: 'nowrap', opacity: n ? 1 : 0.5,
  });
  const arrow = { minWidth: 44, minHeight: 44, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' };

  return (
    <section className="sport-week-card" data-testid="sport-week-card" style={{ display: 'grid', gap: '0.75rem', minWidth: 0 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
        <h2 className="learning-dashboard__section-title" style={{ display: 'flex', alignItems: 'center', gap: 8, margin: 0, marginInlineEnd: 'auto' }}>
          <CalendarRange size={18} /> {t('sport.week.cardTitle')}
        </h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <button type="button" className="btn btn-secondary" style={arrow} aria-label={t('sport.week.prevWeek')} onClick={() => setWeekKey(addDaysKey(weekKey, -7))} data-testid="week-card-prev">
            {isRTL ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            style={{ minHeight: 44, padding: '0 0.6rem', fontWeight: 700 }}
            onClick={() => setWeekKey(currentWeekKey())}
            title={t('sport.week.thisWeek')}
            data-testid="week-card-range"
          >
            <bdi dir="ltr">{weekRangeText(weekKey)}</bdi>
          </button>
          <button type="button" className="btn btn-secondary" style={arrow} aria-label={t('sport.week.nextWeek')} onClick={() => setWeekKey(addDaysKey(weekKey, 7))} data-testid="week-card-next">
            {isRTL ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
          </button>
        </div>
        {onScheduleWeek && (
          <button type="button" className="btn btn-primary" style={{ minHeight: 44, display: 'inline-flex', alignItems: 'center', gap: 6 }} onClick={() => onScheduleWeek(weekKey === currentWeekKey() ? null : weekKey)} data-testid="week-card-schedule">
            <CalendarPlus size={16} /> {t('sport.week.entry')}
          </button>
        )}
      </div>

      {data && data.messages && data.messages.pending > 0 && (
        <span style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)' }} data-testid="week-card-pending">{t('sport.week.messagesPending', { count: data.messages.pending })}</span>
      )}
      {failed && !loading && <span style={{ fontSize: '0.85rem', color: 'var(--danger-700, #b91c1c)' }}>{t('sport.week.loadError')}</span>}

      <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: 4 }}>
        <div className="sport-week-card__days" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(140px, 1fr))', gap: 8, minWidth: 7 * 140 }} data-testid="week-card-days">
          {[0, 1, 2, 3, 4, 5, 6].map((d) => {
            const key = addDaysKey(weekKey, d);
            const isToday = key === today;
            const items = sessions.filter((s) => s.date === key);
            return (
              <div
                key={key}
                data-testid={`week-card-day-${key}`}
                data-today={isToday ? 'true' : undefined}
                style={{
                  display: 'grid', alignContent: 'start', gap: 6, padding: 8, borderRadius: 12, minHeight: 90,
                  border: isToday ? '2px solid var(--color-primary, #355760)' : '1px solid var(--color-border, #e5e7eb)',
                  background: isToday ? 'var(--color-primary-soft, rgba(53,87,96,0.08))' : 'var(--color-surface, #fff)',
                }}
              >
                <span style={{ fontSize: '0.8rem', fontWeight: 700, color: isToday ? 'var(--color-primary, #355760)' : 'var(--color-text-secondary)' }}>
                  {dayTitle(key)}{isToday ? ` · ${t('sport.week.today')}` : ''}
                </span>
                {items.length === 0 && !loading && <span style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)' }}>{t('sport.week.noSessions')}</span>}
                {items.map((s) => {
                  const c = groupColor(s.groupId);
                  return (
                    <button
                      key={s.lessonId}
                      type="button"
                      onClick={() => navigate(`/lessons/${s.lessonId}/attendance`)}
                      data-testid="week-card-session"
                      style={{
                        display: 'grid', gap: 3, textAlign: 'start', padding: '6px 8px', borderRadius: 8, cursor: 'pointer', minHeight: 44,
                        background: c.bg, color: c.fg, border: 'none', borderInlineStart: `4px solid ${c.bar}`, boxShadow: 'none', width: '100%',
                      }}
                    >
                      <span style={{ fontSize: '0.75rem', fontWeight: 700 }}><bdi dir="ltr">{s.startTime}{s.endTime ? `–${s.endTime}` : ''}</bdi></span>
                      <span style={{ fontSize: '0.85rem', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.groupName || s.studentName || t('sport.sessionsCard.privateSession')}</span>
                      {s.coachNames && s.coachNames.length > 0 && <span style={{ fontSize: '0.72rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.coachNames.join(' · ')}</span>}
                      <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4, fontSize: '0.72rem' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}><Users size={12} /> {s.participants}</span>
                        {s.counts && (s.counts.confirmed + s.counts.declined + s.counts.pending) > 0 && ['confirmed', 'declined', 'pending'].map((k) => (
                          <span key={k} style={countChip(k, s.counts[k])} title={t(`sport.sessionsCard.status.${k}`)} data-testid={`week-card-count-${k}`}>{s.counts[k]}</span>
                        ))}
                      </span>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
